// Helpers for the conditional (follow-up) questions feature. Conditional
// questions are stored as flat entries in the same array as their parent,
// linked by `parent_question_id` + `parent_answer`. These pure helpers keep
// the editor logic out of the components (see ADR: pure functions outside
// components).

import { CHECKBOX_MIN_OPTIONS } from "./checkboxQuestions";

export interface ConditionalQuestion {
  id: string;
  type: string;
  // Optional: screening-template questions keep their text in `title`.
  question?: string;
  name?: string;
  options?: string[];
  parent_question_id?: string;
  parent_answer?: string;
}

const YES_NO = "yes_no";
const MULTIPLE_CHOICE = "multiple_choice";
// CRM-1624: select all that apply, in both pre-screening and the survey.
const CHECKBOX = "checkbox";

const YES_NO_TRIGGERS: { value: string; label: string }[] = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" }
];

export const getTopLevelQuestions = <T extends ConditionalQuestion>(
  questions: T[]
): T[] => questions.filter((q) => !q.parent_question_id);

export const getChildren = <T extends ConditionalQuestion>(
  questions: T[],
  parentId: string
): T[] => questions.filter((q) => q.parent_question_id === parentId);

// Groups every question by its parent id in a single pass — use this instead
// of calling getChildren per parent in a loop (each call re-scans the whole
// array, making that O(n²) over the top-level questions).
export const groupChildrenByParentId = <T extends ConditionalQuestion>(
  questions: T[]
): Map<string, T[]> => {
  const byParentId = new Map<string, T[]>();
  questions.forEach((q) => {
    if (!q.parent_question_id) return;
    byParentId.set(q.parent_question_id, [
      ...(byParentId.get(q.parent_question_id) ?? []),
      q
    ]);
  });
  return byParentId;
};

// Options and Checkbox questions are answered from their own list of options.
const hasOptions = (type: string | undefined): boolean =>
  type === MULTIPLE_CHOICE || type === CHECKBOX;

// Only yes_no / multiple_choice / checkbox questions can be parents — free
// text cannot.
export const canHaveConditionals = (type: string | undefined): boolean =>
  type === YES_NO || hasOptions(type);

// How many answers a parent needs before a conditional question can be added
// under it: a Checkbox question needs as many as it can be saved with.
export const minTriggerOptions = (type: string | undefined): number =>
  type === CHECKBOX ? CHECKBOX_MIN_OPTIONS : 1;

// The set of answers that can trigger a conditional child for a given parent.
// A Checkbox parent's child is asked when its trigger is among the answers
// ticked.
export const getTriggerOptions = (
  parent: ConditionalQuestion
): { value: string; label: string }[] => {
  if (parent.type === YES_NO) return YES_NO_TRIGGERS;
  if (hasOptions(parent.type))
    return (parent.options ?? []).map((option) => ({
      value: option,
      label: option
    }));
  return [];
};

// The trigger stays editable after the first child is added, but since every
// child of one parent shares a single condition, changing it must move all of
// them together rather than forking them onto different answers.
export const setTriggerForChildren = <T extends ConditionalQuestion>(
  questions: T[],
  parentId: string,
  trigger: string
): T[] =>
  questions.map((q) =>
    q.parent_question_id === parentId ? { ...q, parent_answer: trigger } : q
  );

const isValidTriggerForParent = (
  parent: ConditionalQuestion,
  parentAnswer: string | undefined
): boolean => {
  if (!parentAnswer) return false;
  if (parent.type === YES_NO)
    return parentAnswer === "yes" || parentAnswer === "no";
  if (hasOptions(parent.type))
    return (parent.options ?? []).includes(parentAnswer);
  return false;
};

// Ids of a parent's children whose trigger is no longer valid after the parent
// changed type or had the triggering option removed.
export const getOrphanedChildIds = <T extends ConditionalQuestion>(
  questions: T[],
  parent: T
): string[] =>
  getChildren(questions, parent.id)
    .filter((child) => !isValidTriggerForParent(parent, child.parent_answer))
    .map((child) => child.id);

// Insert a new child directly after its parent's last existing child so the
// flat array stays grouped (parent followed by its children). The server
// re-normalizes `order` on save, so this only needs to be visually sane.
export const insertChildAfterParent = <T extends ConditionalQuestion>(
  questions: T[],
  parentId: string,
  child: T
): T[] => {
  const parentIndex = questions.findIndex((q) => q.id === parentId);
  if (parentIndex === -1) return [...questions, child];

  let insertAt = parentIndex + 1;
  while (
    insertAt < questions.length &&
    questions[insertAt].parent_question_id === parentId
  )
    insertAt += 1;

  const result = [...questions];
  result.splice(insertAt, 0, child);
  return result;
};

// Remove a question and (if it is a parent) all of its conditional children.
export const removeQuestionAndChildren = <T extends ConditionalQuestion>(
  questions: T[],
  questionId: string
): T[] =>
  questions.filter(
    (q) => q.id !== questionId && q.parent_question_id !== questionId
  );

// Reorder top-level questions, moving each parent's children along with it so
// children stay contiguous beneath their parent. `startIndex`/`endIndex` are
// positions within the top-level list (the only draggable rows).
export const reorderTopLevel = <T extends ConditionalQuestion>(
  questions: T[],
  startIndex: number,
  endIndex: number
): T[] => {
  const childrenByParentId = groupChildrenByParentId(questions);
  const topLevel = getTopLevelQuestions(questions);
  const topLevelIds = new Set(topLevel.map((q) => q.id));
  const orphans = questions.filter(
    (q) => q.parent_question_id && !topLevelIds.has(q.parent_question_id)
  );

  const blocks = topLevel.map((parent) => [
    parent,
    ...(childrenByParentId.get(parent.id) ?? [])
  ]);
  const [moved] = blocks.splice(startIndex, 1);
  blocks.splice(endIndex, 0, moved);
  return [...blocks.flat(), ...orphans];
};
